import axios from "axios";
import { jwtDecode } from "jwt-decode";

const URL = process.env.REACT_APP_API_BASE_URL;

if (!URL) {
  throw new Error("REACT_APP_API_BASE_URL is not defined");
}

const api = axios.create({
  baseURL: URL,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true,
});

const isTokenExpired = (token) => {
  if (!token) return true;
  try {
    const { exp } = jwtDecode(token);
    return Date.now() >= exp * 1000;
  } catch {
    return true;
  }
};

export const refreshAccessToken = async () => {
  try {
    const res = await axios.post(
      `${URL}/auth/refresh_token`,
      {},
      { withCredentials: true },
    );

    if (res.data?.token) {
      localStorage.setItem("token", res.data.token);
      return res.data;
    }

    localStorage.removeItem("token");
    return null;
  } catch (err) {
    console.error("Token refresh failed:", err.response?.data || err.message);
    localStorage.removeItem("token");
    return null;
  }
};

api.interceptors.request.use(async (config) => {
  if (config.url.includes("/auth/refresh_token")) return config;

  let token = localStorage.getItem("token");

  if (token && isTokenExpired(token)) {
    const refreshed = await refreshAccessToken();
    token = refreshed?.token;
  }

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
});

export async function apiRequest(method, url, data = {}, options = {}) {
  try {
    const response = await api.request({
      method,
      url,
      data,
      ...options,
    });

    return { data: response.data, error: null };
  } catch (err) {
    const message =
      err.response?.data?.message ||
      err.response?.data?.error ||
      err.message ||
      "Unknown error";

    console.error(`[API ERROR] ${method?.toUpperCase()} ${url}:`, message);

    if (message && typeof message === "object") {
      return { data: null, error: message };
    }

    if (
      message?.toLowerCase()?.includes("invalid") ||
      message?.toLowerCase()?.includes("expired")
    ) {
      localStorage.removeItem("token");
    }

    return { data: null, error: message };
  }
}

export default api;
